import React, { useEffect, useState, useCallback } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import {
  Database, Download, Upload, Trash2, Plus, RefreshCw, Clock, Shield,
  AlertTriangle, CheckCircle2, X, Loader2, HardDrive, FileArchive,
  Calendar as CalendarIcon, History, FlaskConical, Building2,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Error";
  if (typeof d === "string") return d;
  if (typeof d === "object") return d.message_en || d.msg || JSON.stringify(d);
  return String(d);
};

const fmtBytes = (n) => {
  const x = Number(n || 0);
  if (x < 1024) return `${x} B`;
  if (x < 1024 * 1024) return `${(x / 1024).toFixed(1)} KB`;
  if (x < 1024 * 1024 * 1024) return `${(x / 1024 / 1024).toFixed(1)} MB`;
  return `${(x / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const fmtDate = (iso, isAr) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(isAr ? "ar-EG" : "en-GB", {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const typeBadge = (t, isAr) => {
  const map = {
    full:                 { en: "Full",          ar: "كامل",     cls: "bg-emerald-100 text-emerald-700" },
    database_only:        { en: "Database",      ar: "قاعدة",    cls: "bg-blue-100 text-blue-700" },
    tenant:               { en: "Tenant",        ar: "مستأجر",   cls: "bg-amber-100 text-amber-700" },
    configuration:        { en: "Configuration", ar: "إعدادات",  cls: "bg-violet-100 text-violet-700" },
    pre_restore_snapshot: { en: "Pre-restore",   ar: "قبل-الاستعادة", cls: "bg-stone-200 text-stone-700" },
  };
  const v = map[t] || { en: t, ar: t, cls: "bg-stone-100 text-stone-700" };
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${v.cls}`}>{isAr ? v.ar : v.en}</span>;
};

// ─── Create Backup modal ────────────────────────────────────
const CreateBackupModal = ({ open, onClose, onCreated, tenants, lang }) => {
  const isAr = lang === "ar";
  const [type, setType] = useState("full");
  const [tenantId, setTenantId] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (type === "tenant" && !tenantId) {
      toast.error(isAr ? "اختر المستأجر" : "Pick a tenant");
      return;
    }
    setLoading(true);
    try {
      const body = { type, note };
      if (type === "tenant") body.tenant_id = tenantId;
      const r = await api.post("/admin/backups", body);
      toast.success(isAr ? "تم إنشاء النسخة" : "Backup created");
      onCreated(r.data);
      onClose();
      setType("full"); setTenantId(""); setNote("");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="create-backup-modal" onMouseDown={(e) => onBackdropMouseDown(e, onClose)}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6" onMouseDown={onModalPanelMouseDown}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl text-stone-800 flex items-center gap-2">
            <FileArchive size={20} className="text-amber-600"/>
            {isAr ? "إنشاء نسخة احتياطية" : "Create Backup"}
          </h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600"><X size={20}/></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs text-stone-500 mb-2 block">{isAr ? "نوع النسخة" : "Type"}</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: "full",          ar: "كامل (كل البيانات)",   en: "Full (everything)"  },
                { v: "database_only", ar: "قاعدة البيانات",       en: "Database only"      },
                { v: "tenant",        ar: "مستأجر محدد",         en: "Specific tenant"    },
                { v: "configuration", ar: "الإعدادات فقط",       en: "Configuration only" },
              ].map(o => (
                <button key={o.v} onClick={() => setType(o.v)}
                  data-testid={`backup-type-${o.v}`}
                  className={`text-xs px-3 py-2.5 rounded-lg border-2 transition text-start ${
                    type === o.v
                      ? "border-amber-500 bg-amber-50 text-amber-800 font-semibold"
                      : "border-stone-200 hover:border-stone-300 text-stone-600"
                  }`}>
                  {isAr ? o.ar : o.en}
                </button>
              ))}
            </div>
          </div>

          {type === "tenant" && (
            <div>
              <label className="text-xs text-stone-500 mb-1 block">{isAr ? "المستأجر" : "Tenant"}</label>
              <select value={tenantId} onChange={(e) => setTenantId(e.target.value)}
                data-testid="backup-tenant-select"
                className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">{isAr ? "اختر..." : "Select..."}</option>
                {tenants.map(t => (
                  <option key={t.tenant_id} value={t.tenant_id}>
                    {(typeof t.name === "object" ? (isAr ? t.name.ar : t.name.en) : t.name) || t.tenant_id}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="text-xs text-stone-500 mb-1 block">{isAr ? "ملاحظة (اختياري)" : "Note (optional)"}</label>
            <input value={note} onChange={(e) => setNote(e.target.value)}
              data-testid="backup-note-input"
              placeholder={isAr ? "وصف موجز..." : "Brief description..."}
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm"/>
          </div>

          <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 text-xs text-stone-600 flex gap-2">
            <Shield size={14} className="text-amber-600 shrink-0 mt-0.5"/>
            <span>{isAr
              ? "تُحفظ النسخة بصيغة JSON مضغوطة. تحافظ النسخ على عزل المستأجرين بالكامل."
              : "Archive is stored as compressed JSON. Tenant isolation is preserved on every document."}</span>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} data-testid="create-backup-cancel"
            className="px-4 py-2 rounded-lg text-sm text-stone-600 hover:bg-stone-100">
            {isAr ? "إلغاء" : "Cancel"}
          </button>
          <button onClick={submit} disabled={loading} data-testid="create-backup-submit"
            className="btn-gold px-5 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50">
            {loading ? <Loader2 size={14} className="animate-spin"/> : <Plus size={14}/>}
            {isAr ? "إنشاء" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Restore Modal (with "RESTORE" confirmation) ────────────
const RestoreModal = ({ open, onClose, backup, onRestored, lang }) => {
  const isAr = lang === "ar";
  const [mode, setMode] = useState("merge");
  const [confirm, setConfirm] = useState("");
  const [ack, setAck] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dryRun, setDryRun] = useState(null);

  useEffect(() => {
    if (open) { setMode("merge"); setConfirm(""); setAck(false); setDryRun(null); }
  }, [open]);

  const runDryRun = async () => {
    setLoading(true); setDryRun(null);
    try {
      const r = await api.post(`/admin/backups/${backup.backup_id}/restore`, { mode: "dry_run" });
      setDryRun(r.data);
      toast.success(isAr ? "تمت المحاكاة" : "Dry-run complete");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };

  const doRestore = async () => {
    if (confirm !== "RESTORE") {
      toast.error(isAr ? "اكتب RESTORE للتأكيد" : "Type RESTORE to confirm");
      return;
    }
    if (!ack) {
      toast.error(isAr ? "يرجى التأكيد بقبول التحذير" : "Please acknowledge the warning");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post(`/admin/backups/${backup.backup_id}/restore`, {
        mode, confirmation: "RESTORE", acknowledge: true,
      });
      toast.success(isAr ? "تمت الاستعادة بنجاح" : "Restore completed");
      onRestored(r.data);
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };

  if (!open || !backup) return null;
  const dryTotal = dryRun ? Object.values(dryRun.collections || {})
    .reduce((s, c) => s + (c.would_insert || 0), 0) : 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="restore-modal" onMouseDown={(e) => onBackdropMouseDown(e, onClose)}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-6 max-h-[92vh] overflow-y-auto" onMouseDown={onModalPanelMouseDown}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl text-stone-800 flex items-center gap-2">
            <Upload size={20} className="text-rose-600"/>
            {isAr ? "استعادة من نسخة احتياطية" : "Restore from Backup"}
          </h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600"><X size={20}/></button>
        </div>

        <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-800 flex gap-2 mb-4">
          <AlertTriangle size={16} className="text-rose-600 shrink-0 mt-0.5"/>
          <div>
            <div className="font-bold mb-1">{isAr ? "تحذير حرج" : "Critical Warning"}</div>
            <div>{isAr
              ? "هذه العملية ستعدل بيانات الإنتاج. سنأخذ صورة احتياطية تلقائية قبل الاستعادة."
              : "This will modify production data. A pre-restore snapshot will be auto-created."}</div>
          </div>
        </div>

        <div className="space-y-1.5 text-xs bg-stone-50 rounded-lg p-3 mb-4">
          <div className="flex justify-between"><span className="text-stone-500">{isAr ? "المعرف" : "ID"}</span><span className="font-mono">{backup.backup_id}</span></div>
          <div className="flex justify-between"><span className="text-stone-500">{isAr ? "النوع" : "Type"}</span><span>{typeBadge(backup.type, isAr)}</span></div>
          <div className="flex justify-between"><span className="text-stone-500">{isAr ? "الحجم" : "Size"}</span><span>{fmtBytes(backup.size_bytes)}</span></div>
          <div className="flex justify-between"><span className="text-stone-500">{isAr ? "المستندات" : "Documents"}</span><span>{backup.doc_count?.toLocaleString()}</span></div>
          <div className="flex justify-between"><span className="text-stone-500">{isAr ? "أنشئ" : "Created"}</span><span>{fmtDate(backup.created_at, isAr)}</span></div>
        </div>

        <div>
          <label className="text-xs text-stone-500 mb-2 block">{isAr ? "وضع الاستعادة" : "Restore mode"}</label>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setMode("merge")} data-testid="restore-mode-merge"
              className={`text-xs px-3 py-2.5 rounded-lg border-2 text-start ${mode === "merge"
                ? "border-amber-500 bg-amber-50 text-amber-800 font-semibold"
                : "border-stone-200 text-stone-600"}`}>
              <div className="font-semibold">{isAr ? "دمج" : "Merge"}</div>
              <div className="text-[10px] mt-0.5 opacity-80">{isAr ? "إضافة المفقود فقط — أكثر أمانًا" : "Insert missing only — safer"}</div>
            </button>
            <button onClick={() => setMode("replace")} data-testid="restore-mode-replace"
              className={`text-xs px-3 py-2.5 rounded-lg border-2 text-start ${mode === "replace"
                ? "border-rose-500 bg-rose-50 text-rose-800 font-semibold"
                : "border-stone-200 text-stone-600"}`}>
              <div className="font-semibold">{isAr ? "استبدال" : "Replace"}</div>
              <div className="text-[10px] mt-0.5 opacity-80">{isAr ? "حذف ثم إدراج — يمسح البيانات الحالية" : "Wipe + insert — destroys current data"}</div>
            </button>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between gap-2">
          <button onClick={runDryRun} disabled={loading} data-testid="restore-dry-run"
            className="px-3 py-2 rounded-lg text-xs border border-stone-200 hover:bg-stone-50 flex items-center gap-1.5 disabled:opacity-50">
            <FlaskConical size={14}/>{isAr ? "محاكاة جافة" : "Dry-Run Preview"}
          </button>
          {dryRun && (
            <div className="text-xs text-stone-600">
              {isAr ? "سيُدرج" : "Would insert"}: <strong className="text-emerald-700">{dryTotal.toLocaleString()}</strong> {isAr ? "مستند عبر" : "docs across"} {Object.keys(dryRun.collections || {}).length} {isAr ? "مجموعة" : "collections"}
            </div>
          )}
        </div>

        <div className="mt-4 border-t border-stone-200 pt-4">
          <label className="text-xs text-stone-500 mb-1 block">
            {isAr ? "اكتب " : "Type "}<span className="font-mono bg-stone-100 px-1.5 py-0.5 rounded text-stone-800">RESTORE</span>{isAr ? " للتأكيد" : " to confirm"}
          </label>
          <input value={confirm} onChange={(e) => setConfirm(e.target.value)}
            data-testid="restore-confirm-input"
            placeholder="RESTORE"
            className="w-full border-2 border-stone-200 rounded-lg px-3 py-2 text-sm font-mono focus:border-rose-400 outline-none"/>
          <label className="flex items-start gap-2 mt-3 text-xs text-stone-700 cursor-pointer">
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)}
              data-testid="restore-ack-checkbox" className="mt-0.5"/>
            <span>{isAr
              ? "أؤكد فهمي لتأثير هذه العملية وأنه سيتم تعديل بيانات الإنتاج."
              : "I understand this will modify live production data and is irreversible."}</span>
          </label>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-stone-600 hover:bg-stone-100">
            {isAr ? "إلغاء" : "Cancel"}
          </button>
          <button onClick={doRestore} disabled={loading || confirm !== "RESTORE" || !ack}
            data-testid="restore-execute"
            className="px-5 py-2 rounded-lg text-sm bg-rose-600 text-white hover:bg-rose-700 flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
            {loading ? <Loader2 size={14} className="animate-spin"/> : <Upload size={14}/>}
            {isAr ? "تنفيذ الاستعادة" : "Execute Restore"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Schedule Modal ──────────────────────────────────────────
const ScheduleModal = ({ open, onClose, schedule, onSaved, lang }) => {
  const isAr = lang === "ar";
  const [data, setData] = useState({
    enabled: false, frequency: "daily", hour: 2, retention_days: 30, max_backups: 10,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (open && schedule) setData((s) => ({ ...s, ...schedule })); }, [open, schedule]);

  const save = async () => {
    setLoading(true);
    try {
      await api.put("/admin/backups/schedule", data);
      toast.success(isAr ? "تم حفظ الجدول" : "Schedule saved");
      onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="schedule-modal" onMouseDown={(e) => onBackdropMouseDown(e, onClose)}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onMouseDown={onModalPanelMouseDown}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl text-stone-800 flex items-center gap-2">
            <CalendarIcon size={20} className="text-amber-600"/>
            {isAr ? "الجدولة التلقائية" : "Automated Schedule"}
          </h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600"><X size={20}/></button>
        </div>
        <div className="space-y-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={data.enabled} onChange={(e) => setData({ ...data, enabled: e.target.checked })}
              data-testid="schedule-enabled-toggle"/>
            <span className="text-sm">{isAr ? "تمكين الجدولة" : "Enable scheduled backups"}</span>
          </label>
          <div>
            <label className="text-xs text-stone-500 mb-1 block">{isAr ? "التكرار" : "Frequency"}</label>
            <select value={data.frequency} onChange={(e) => setData({ ...data, frequency: e.target.value })}
              data-testid="schedule-frequency"
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm">
              <option value="daily">{isAr ? "يومي" : "Daily"}</option>
              <option value="weekly">{isAr ? "أسبوعي" : "Weekly"}</option>
              <option value="monthly">{isAr ? "شهري" : "Monthly"}</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-stone-500 mb-1 block">{isAr ? "ساعة التشغيل (UTC)" : "Hour (UTC)"}</label>
              <input type="number" min="0" max="23" value={data.hour}
                data-testid="schedule-hour"
                onChange={(e) => setData({ ...data, hour: parseInt(e.target.value || "0", 10) })}
                className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm"/>
            </div>
            <div>
              <label className="text-xs text-stone-500 mb-1 block">{isAr ? "الاحتفاظ (أيام)" : "Retention (days)"}</label>
              <input type="number" min="1" value={data.retention_days}
                data-testid="schedule-retention"
                onChange={(e) => setData({ ...data, retention_days: parseInt(e.target.value || "1", 10) })}
                className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm"/>
            </div>
          </div>
          <div>
            <label className="text-xs text-stone-500 mb-1 block">{isAr ? "الحد الأقصى للنسخ" : "Max backups"}</label>
            <input type="number" min="1" value={data.max_backups}
              data-testid="schedule-max-backups"
              onChange={(e) => setData({ ...data, max_backups: parseInt(e.target.value || "1", 10) })}
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm"/>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-stone-600 hover:bg-stone-100">
            {isAr ? "إلغاء" : "Cancel"}
          </button>
          <button onClick={save} disabled={loading} data-testid="schedule-save"
            className="btn-gold px-5 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50">
            {loading ? <Loader2 size={14} className="animate-spin"/> : <CheckCircle2 size={14}/>}
            {isAr ? "حفظ" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Audit Log Modal ─────────────────────────────────────────
const AuditLogModal = ({ open, onClose, lang }) => {
  const isAr = lang === "ar";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/backups/audit-log");
      setRows(r.data || []);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="audit-modal" onMouseDown={(e) => onBackdropMouseDown(e, onClose)}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl p-6 max-h-[90vh] flex flex-col" onMouseDown={onModalPanelMouseDown}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl text-stone-800 flex items-center gap-2">
            <History size={20} className="text-amber-600"/>
            {isAr ? "سجل تدقيق النسخ الاحتياطية" : "Backup Audit Log"}
          </h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600"><X size={20}/></button>
        </div>
        <div className="overflow-y-auto flex-1">
          {loading ? <div className="p-8 text-center"><Loader2 className="animate-spin inline" size={20}/></div> :
            rows.length === 0 ? <div className="p-8 text-center text-stone-500 text-sm">{isAr ? "لا توجد سجلات" : "No entries yet"}</div> :
            <table className="w-full text-xs" data-testid="audit-table">
              <thead className="bg-stone-50 text-stone-500 uppercase">
                <tr>
                  <th className="text-start py-2 px-2">{isAr ? "الوقت" : "Time"}</th>
                  <th className="text-start py-2 px-2">{isAr ? "الإجراء" : "Action"}</th>
                  <th className="text-start py-2 px-2">{isAr ? "المستخدم" : "User"}</th>
                  <th className="text-start py-2 px-2">{isAr ? "النسخة" : "Backup"}</th>
                  <th className="text-start py-2 px-2">{isAr ? "الحالة" : "Status"}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.audit_id} className="border-t border-stone-100">
                    <td className="py-2 px-2 text-stone-600">{fmtDate(r.timestamp, isAr)}</td>
                    <td className="py-2 px-2 font-mono text-stone-800">{r.action}</td>
                    <td className="py-2 px-2">{r.username || "—"}</td>
                    <td className="py-2 px-2 font-mono text-stone-500">{r.backup_id || "—"}</td>
                    <td className="py-2 px-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                        r.status === "success" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
                      }`}>{r.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          }
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ──────────────────────────────────────────────
export default function PlatformAdminBackups() {
  const { lang } = useLanguage();
  const { confirm, ConfirmDialogSlot } = useConfirmDialog();
  const isAr = lang === "ar";

  const [stats, setStats] = useState(null);
  const [backups, setBackups] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, b, t, sch] = await Promise.all([
        api.get("/admin/backups/stats"),
        api.get("/admin/backups"),
        api.get("/admin/tenants").catch(() => ({ data: { items: [] } })),
        api.get("/admin/backups/schedule"),
      ]);
      setStats(s.data);
      setBackups(b.data || []);
      const tlist = t.data?.items || t.data || [];
      setTenants(Array.isArray(tlist) ? tlist : []);
      setSchedule(sch.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const onDownload = async (b) => {
    try {
      const url = `${API_BASE}/admin/backups/${b.backup_id}/download`;
      const r = await fetch(url, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${b.backup_id}.json.gz`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
      toast.success(isAr ? "تم التنزيل" : "Downloaded");
    } catch (e) { toast.error(e.message || "Download failed"); }
  };

  const onDelete = async (b) => {
    const ok = await confirm({
      title: isAr
        ? `حذف النسخة ${b.backup_id}؟ لا يمكن التراجع.`
        : `Delete backup ${b.backup_id}? This cannot be undone.`,
      confirmLabel: isAr ? "حذف" : "Delete",
      cancelLabel: isAr ? "إلغاء" : "Cancel",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/backups/${b.backup_id}`);
      toast.success(isAr ? "تم الحذف" : "Deleted");
      loadAll();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const filtered = filterType ? backups.filter((b) => b.type === filterType) : backups;

  return (
    <div className="p-6 max-w-7xl mx-auto" dir={isAr ? "rtl" : "ltr"} data-testid="platform-backups-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl text-stone-800 flex items-center gap-2">
            <Database size={28} className="text-amber-600"/>
            {isAr ? "النسخ الاحتياطي والاستعادة" : "Backup & Restore"}
          </h1>
          <p className="text-sm text-stone-500 mt-1">
            {isAr ? "إدارة كاملة لنسخ المنصة الاحتياطية مع عزل المستأجرين." : "Platform-level backup management with strict tenant isolation."}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={loadAll} data-testid="backups-refresh"
            className="px-3 py-2 rounded-lg border border-stone-200 hover:bg-stone-50 flex items-center gap-1.5 text-xs">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""}/>{isAr ? "تحديث" : "Refresh"}
          </button>
          <button onClick={() => setAuditOpen(true)} data-testid="backups-audit-btn"
            className="px-3 py-2 rounded-lg border border-stone-200 hover:bg-stone-50 flex items-center gap-1.5 text-xs">
            <History size={14}/>{isAr ? "السجل" : "Audit Log"}
          </button>
          <button onClick={() => setScheduleOpen(true)} data-testid="backups-schedule-btn"
            className="px-3 py-2 rounded-lg border border-stone-200 hover:bg-stone-50 flex items-center gap-1.5 text-xs">
            <CalendarIcon size={14}/>{isAr ? "الجدولة" : "Schedule"}
          </button>
          <button onClick={() => setCreateOpen(true)} data-testid="backups-create-btn"
            className="btn-gold px-4 py-2 rounded-lg flex items-center gap-1.5 text-sm">
            <Plus size={14}/>{isAr ? "نسخة جديدة" : "New Backup"}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <StatCard icon={FileArchive} label={isAr ? "إجمالي النسخ" : "Total Backups"}
          value={stats?.total_backups ?? "—"} color="amber" testid="stat-total"/>
        <StatCard icon={HardDrive} label={isAr ? "حجم النسخ" : "Storage Used"}
          value={fmtBytes(stats?.total_size_bytes || 0)} color="blue" testid="stat-storage"/>
        <StatCard icon={Database} label={isAr ? "حجم قاعدة البيانات" : "Database Size"}
          value={fmtBytes(stats?.database_size_bytes || 0)} color="violet" testid="stat-dbsize"/>
        <StatCard icon={Building2} label={isAr ? "المستأجرون" : "Tenants"}
          value={stats?.tenants_count ?? "—"} color="emerald" testid="stat-tenants"/>
      </div>

      {/* Schedule status */}
      {schedule?.enabled && (
        <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800 flex items-center gap-2"
          data-testid="schedule-status-banner">
          <Clock size={14}/>
          <span>{isAr
            ? `الجدولة مفعّلة — ${schedule.frequency === "daily" ? "يوميًا" : schedule.frequency === "weekly" ? "أسبوعيًا" : "شهريًا"} عند الساعة ${schedule.hour}:00 UTC. الاحتفاظ ${schedule.retention_days} يومًا.`
            : `Scheduled backups active — ${schedule.frequency} at ${String(schedule.hour).padStart(2, "0")}:00 UTC. Retention ${schedule.retention_days} days.`}</span>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 mb-3 text-xs">
        {[
          { v: "",                ar: "الكل",        en: "All" },
          { v: "full",            ar: "كامل",        en: "Full" },
          { v: "database_only",   ar: "قاعدة",       en: "Database" },
          { v: "tenant",          ar: "مستأجر",      en: "Tenant" },
          { v: "configuration",   ar: "إعدادات",     en: "Configuration" },
          { v: "pre_restore_snapshot", ar: "قبل-الاستعادة", en: "Pre-restore" },
        ].map(o => (
          <button key={o.v || "all"} onClick={() => setFilterType(o.v)}
            data-testid={`backups-filter-${o.v || "all"}`}
            className={`px-3 py-1.5 rounded-full ${filterType === o.v
              ? "bg-amber-100 text-amber-800 font-semibold"
              : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>
            {isAr ? o.ar : o.en}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><Loader2 size={28} className="animate-spin mx-auto text-amber-600"/></div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-stone-400">
            <FileArchive size={40} className="mx-auto mb-2 opacity-40"/>
            <div className="text-sm">{isAr ? "لا توجد نسخ احتياطية بعد. ابدأ بإنشاء واحدة." : "No backups yet. Create your first one."}</div>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="backups-table">
            <thead className="bg-stone-50 text-stone-500 uppercase text-[10px] tracking-wider">
              <tr>
                <th className="text-start py-3 px-4">{isAr ? "المعرف" : "ID"}</th>
                <th className="text-start py-3 px-4">{isAr ? "النوع" : "Type"}</th>
                <th className="text-start py-3 px-4">{isAr ? "الحجم" : "Size"}</th>
                <th className="text-start py-3 px-4">{isAr ? "المستندات" : "Docs"}</th>
                <th className="text-start py-3 px-4">{isAr ? "أنشئ" : "Created"}</th>
                <th className="text-start py-3 px-4">{isAr ? "بواسطة" : "By"}</th>
                <th className="text-end py-3 px-4">{isAr ? "إجراءات" : "Actions"}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => (
                <tr key={b.backup_id} className="border-t border-stone-100 hover:bg-stone-50/50" data-testid={`backup-row-${b.backup_id}`}>
                  <td className="py-3 px-4">
                    <div className="font-mono text-xs text-stone-700">{b.backup_id}</div>
                    {b.note && <div className="text-[10px] text-stone-400 mt-0.5">{b.note}</div>}
                  </td>
                  <td className="py-3 px-4">{typeBadge(b.type, isAr)}</td>
                  <td className="py-3 px-4 text-xs text-stone-600 num-display">{fmtBytes(b.size_bytes)}</td>
                  <td className="py-3 px-4 text-xs text-stone-600 num-display">{(b.doc_count || 0).toLocaleString()}</td>
                  <td className="py-3 px-4 text-xs text-stone-600">{fmtDate(b.created_at, isAr)}</td>
                  <td className="py-3 px-4 text-xs text-stone-500">{b.created_by_username || "—"}</td>
                  <td className="py-3 px-4 text-end">
                    <div className="inline-flex gap-1">
                      <button onClick={() => onDownload(b)} data-testid={`backup-download-${b.backup_id}`}
                        title={isAr ? "تنزيل" : "Download"}
                        className="p-1.5 rounded hover:bg-stone-200 text-stone-600">
                        <Download size={14}/>
                      </button>
                      <button onClick={() => { setRestoreTarget(b); setRestoreOpen(true); }}
                        data-testid={`backup-restore-${b.backup_id}`}
                        title={isAr ? "استعادة" : "Restore"}
                        className="p-1.5 rounded hover:bg-rose-50 text-rose-600">
                        <Upload size={14}/>
                      </button>
                      <button onClick={() => onDelete(b)} data-testid={`backup-delete-${b.backup_id}`}
                        title={isAr ? "حذف" : "Delete"}
                        className="p-1.5 rounded hover:bg-stone-200 text-stone-500">
                        <Trash2 size={14}/>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modals */}
      <CreateBackupModal open={createOpen} onClose={() => setCreateOpen(false)}
        onCreated={() => loadAll()} tenants={tenants} lang={lang}/>
      <RestoreModal open={restoreOpen} onClose={() => setRestoreOpen(false)}
        backup={restoreTarget} onRestored={() => loadAll()} lang={lang}/>
      <ScheduleModal open={scheduleOpen} onClose={() => setScheduleOpen(false)}
        schedule={schedule} onSaved={() => loadAll()} lang={lang}/>
      <AuditLogModal open={auditOpen} onClose={() => setAuditOpen(false)} lang={lang}/>
      <ConfirmDialogSlot />
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, color, testid }) => {
  const cls = {
    amber:   "from-amber-50 to-white border-amber-100 text-amber-700",
    blue:    "from-blue-50 to-white border-blue-100 text-blue-700",
    violet:  "from-violet-50 to-white border-violet-100 text-violet-700",
    emerald: "from-emerald-50 to-white border-emerald-100 text-emerald-700",
  }[color] || "from-stone-50 to-white border-stone-100 text-stone-700";
  return (
    <div className={`bg-gradient-to-br ${cls} border rounded-xl p-4`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-wider opacity-70">{label}</div>
          <div className="text-2xl font-bold mt-1 num-display">{value}</div>
        </div>
        <Icon size={28} className="opacity-50"/>
      </div>
    </div>
  );
};
